/* =====================================================================
 * HitlQuestionCard — 에이전트가 되물은 질문을 컴포저 위에 띄우는 카드.
 *
 * Interactive 모드에서 턴이 사용자 입력을 기다리며 멈췄을 때 렌더된다.
 * 마지막 행은 항상 비어 있는 자유입력 항목이라, 제시된 선택지가 마음에
 * 들지 않으면 사용자가 직접 답을 적을 수 있다.
 * ===================================================================== */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Check, X, ArrowUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { UserInputRequest } from '@auw/workspace-utils';
import { isSubmittableSelection } from '@auw/workspace-utils';

export interface HitlQuestionCardProps {
  request: UserInputRequest;
  /** 제출 — 선택한 옵션 id 와 자유입력 텍스트. */
  onSubmit: (selection: { selectedOptionIds: string[]; freeText: string }) => void;
  /** 건너뛰기 / 닫기. */
  onSkip: () => void;
  disabled?: boolean;
}

/** 자유입력 행의 가상 인덱스 (옵션 다음 자리). */
const FREE_TEXT_ROW = -1;

export const HitlQuestionCard: React.FC<HitlQuestionCardProps> = ({
  request,
  onSubmit,
  onSkip,
  disabled = false,
}) => {
  const { t } = useTranslation('common');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [freeText, setFreeText] = useState('');
  const [focusedRow, setFocusedRow] = useState(0);
  const freeTextRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const options = request.options ?? [];
  const multi = Boolean(request.multi_select);
  const approval = Boolean(request.approval_preview);
  const freeTextAllowed = request.allow_free_text !== false;
  const skipLabel = t(approval ? 'mcpApproval.deny' : 'hitl.skip');
  const formatPreviewValue = (key: string, value: unknown): string => {
    if (key === 'dry_run' && typeof value === 'boolean') {
      return t(value ? 'mcpApproval.dryRun' : 'mcpApproval.liveSend');
    }
    if (key === 'recipients' && Array.isArray(value)) {
      return value.map((row) => typeof row === 'object' && row !== null && 'emailAddress' in row
        ? `${t(`mcpApproval.fields.${String(row.recipientType).toLowerCase()}`)}: ${row.emailAddress}`
        : String(row)).join('\n');
    }
    if (key === 'attachments' && Array.isArray(value)) {
      if (!value.length) return t('mcpApproval.noAttachments');
      return value.map((row) => typeof row === 'object' && row !== null && 'filename' in row
        ? `${row.filename} (${row.size_bytes} bytes)\nSHA-256: ${row.sha256}`
        : JSON.stringify(row)).join('\n\n');
    }
    return typeof value === 'string' ? value : JSON.stringify(value, null, 2) ?? '';
  };

  /* 자유입력에 무언가 적으면 그것이 곧 답이므로 옵션 선택과 공존시킨다
   * (스크린샷의 마지막 행처럼 "직접 쓴 답" 도 하나의 선택지로 취급). */
  const freeTextActive = freeText.trim().length > 0;

  const selection = useMemo(
    () => ({ selectedOptionIds: selectedIds, freeText }),
    [selectedIds, freeText],
  );
  const canSubmit = !disabled && isSubmittableSelection(selection);

  const toggleOption = useCallback(
    (optionId: string) => {
      setSelectedIds((prev) => {
        if (multi) {
          return prev.includes(optionId)
            ? prev.filter((id) => id !== optionId)
            : [...prev, optionId];
        }
        return prev.includes(optionId) ? [] : [optionId];
      });
    },
    [multi],
  );

  const submit = useCallback(() => {
    if (!canSubmit) return;
    onSubmit({ selectedOptionIds: selectedIds, freeText: freeText.trim() });
  }, [canSubmit, freeText, onSubmit, selectedIds]);

  /* 카드가 뜨면 첫 행에 포커스를 준다. 컴포저 입력을 뺏지 않도록
   * 컨테이너에만 포커스하고, 키 입력은 여기서 처리한다. */
  useEffect(() => {
    containerRef.current?.focus();
  }, [request.request_id]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    const lastRow = options.length - 1;

    if (e.key === 'Escape') {
      e.preventDefault();
      if (request.allow_skip) onSkip();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedRow((prev) => (prev === FREE_TEXT_ROW ? 0 : prev >= lastRow ? (freeTextAllowed ? FREE_TEXT_ROW : 0) : prev + 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedRow((prev) => (prev === FREE_TEXT_ROW ? lastRow : prev <= 0 ? (freeTextAllowed ? FREE_TEXT_ROW : lastRow) : prev - 1));
      return;
    }
    if (e.key === 'Enter') {
      /* 자유입력 행에서의 Enter 는 곧바로 제출 — 사용자가 답을 다 적은 상태다. */
      if (focusedRow === FREE_TEXT_ROW) {
        e.preventDefault();
        submit();
        return;
      }
      e.preventDefault();
      const option = options[focusedRow];
      if (!option) return;
      if (multi) {
        toggleOption(option.id);
      } else {
        onSubmit({ selectedOptionIds: [option.id], freeText: freeText.trim() });
      }
    }
  };

  useEffect(() => {
    if (focusedRow === FREE_TEXT_ROW) freeTextRef.current?.focus();
  }, [focusedRow]);

  const selectedCount = selectedIds.length + (freeTextActive ? 1 : 0);

  return (
    <Card
      ref={containerRef}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      role="group"
      aria-label={request.header || t('hitl.ariaLabel')}
    >
      <Header>
        <Question>{request.question}</Question>
        {request.allow_skip && <CloseBtn type="button" onClick={onSkip} aria-label={skipLabel} disabled={disabled}>
          <X size={16} />
        </CloseBtn>}
      </Header>

      {request.approval_preview && (
        <ApprovalPreview aria-label={t('mcpApproval.preview')}>
          <strong>{request.approval_preview.title || request.approval_preview.tool}</strong>
          {typeof request.approval_preview.arguments.dry_run === 'boolean' && (
            <p role="note">{formatPreviewValue('dry_run', request.approval_preview.arguments.dry_run)}</p>
          )}
          <dl>
            {Object.entries(request.approval_preview.arguments).filter(([key]) => key !== 'dry_run').map(([key, value]) => (
              <React.Fragment key={key}>
                <dt>{t(`mcpApproval.fields.${key}`, { defaultValue: key })}</dt>
                <dd>{formatPreviewValue(key, value)}</dd>
              </React.Fragment>
            ))}
          </dl>
        </ApprovalPreview>
      )}

      <Rows>
        {options.map((option, index) => {
          const checked = selectedIds.includes(option.id);
          return (
            <Row
              key={option.id}
              type="button"
              $focused={focusedRow === index}
              $checked={checked}
              disabled={disabled}
              onMouseEnter={() => setFocusedRow(index)}
              onClick={() => (multi ? toggleOption(option.id) : onSubmit({ selectedOptionIds: [option.id], freeText: freeText.trim() }))}
              aria-pressed={checked}
            >
              <Box $checked={checked} $round={!multi}>
                {checked && <Check size={12} strokeWidth={3} />}
              </Box>
              <RowText>
                <RowLabel>{option.label}</RowLabel>
                {option.description && <RowDesc>{option.description}</RowDesc>}
              </RowText>
            </Row>
          );
        })}

        {/* 마지막 행: 항상 비어 있는 자유입력 (사용자 결정) */}
        {freeTextAllowed && <Row
          as="div"
          $focused={focusedRow === FREE_TEXT_ROW}
          $checked={freeTextActive}
          onMouseEnter={() => setFocusedRow(FREE_TEXT_ROW)}
        >
          <Box $checked={freeTextActive} $round={!multi}>
            {freeTextActive && <Check size={12} strokeWidth={3} />}
          </Box>
          <FreeTextInput
            ref={freeTextRef}
            value={freeText}
            disabled={disabled}
            placeholder={t('hitl.freeTextPlaceholder')}
            onChange={(e) => setFreeText(e.target.value)}
            onFocus={() => setFocusedRow(FREE_TEXT_ROW)}
          />
        </Row>}
      </Rows>

      <Footer>
        <FooterHint>
          {selectedCount > 0
            ? t('hitl.selectedCount', { count: selectedCount })
            : t('hitl.hintKeys')}
        </FooterHint>
        <FooterActions>
          {request.allow_skip && <SkipBtn type="button" onClick={onSkip} disabled={disabled}>
            {skipLabel}
          </SkipBtn>}
          <SubmitBtn
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            aria-label={t('hitl.submit')}
          >
            <ArrowUp size={16} strokeWidth={2.5} />
          </SubmitBtn>
        </FooterActions>
      </Footer>
    </Card>
  );
};

const ApprovalPreview = styled.div`
  margin: 0 12px 8px;
  max-height: 240px;
  overflow: auto;
  font-size: 12px;
  dl { margin: 8px 0 0; }
  dt { opacity: 0.7; margin-top: 8px; }
  dd { white-space: pre-wrap; overflow-wrap: anywhere; margin: 3px 0 0; }
`;

/* ---------------------------------------------------------------- */
/* styles — MessageInput 의 팝업 계열과 같은 표면 언어를 따른다.       */
/* ---------------------------------------------------------------- */

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 14px;
  border: 0.5px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)'};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(40, 40, 42, 0.96)' : 'rgba(255, 255, 255, 0.96)'};
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 8px 32px rgba(0,0,0,0.45), 0 2px 8px rgba(0,0,0,0.3)'
      : '0 8px 32px rgba(0,0,0,0.10), 0 2px 8px rgba(0,0,0,0.06)'};
  animation: ${fadeIn} 0.15s ease;
  outline: none;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 12px 8px 14px;
`;

const Question = styled.div`
  flex: 1;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.button<{ $focused: boolean; $checked: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 14px;
  border: none;
  border-top: 0.5px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)'};
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.fast};

  ${({ $focused, theme }) =>
    $focused &&
    css`
      background: ${theme.mode === 'dark'
        ? 'rgba(255,255,255,0.05)'
        : 'rgba(0,0,0,0.03)'};
    `}

  &:disabled { cursor: default; opacity: 0.5; }
`;

const Box = styled.span<{ $checked: boolean; $round: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: ${({ $round }) => ($round ? '50%' : '5px')};
  border: 1.5px solid ${({ theme, $checked }) =>
    $checked ? theme.colors.brand : theme.colors.border};
  background: ${({ theme, $checked }) => ($checked ? theme.colors.brand : 'transparent')};
  color: ${({ theme }) => theme.colors.textInverse};
  transition: all ${({ theme }) => theme.transitions.fast};
`;

const RowText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const RowLabel = styled.span`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: 1.4;
`;

const RowDesc = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  line-height: 1.4;
`;

const FreeTextInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  outline: none;
  font-size: 0.875rem;
  font-family: inherit;
  color: ${({ theme }) => theme.colors.textPrimary};

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px 8px 14px;
  border-top: 0.5px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)'};
`;

const FooterHint = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const FooterActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SkipBtn = styled.button`
  padding: 5px 10px;
  border: none;
  border-radius: 8px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)'};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.75rem;
  font-family: inherit;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.textPrimary};
  }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const SubmitBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.brand};
  color: ${({ theme }) => theme.colors.textInverse};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.brandHover};
  }
  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
`;

export default HitlQuestionCard;
